import type { RecentShare } from '../lib/storage';

export type ShareResponse = Omit<RecentShare, 'deleteToken'>;

type ShareErrorBody = { error?: unknown };

export class ShareApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ShareApiError';
    this.status = status;
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

export function createDeleteToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

function isShareResponse(value: unknown): value is ShareResponse {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<ShareResponse>;
  return typeof response.url === 'string' && typeof response.idleExpiresAt === 'string';
}

export async function uploadShare(
  apiOrigin: string,
  blob: Blob,
  turnstileToken: string,
  idempotencyKey: string,
  deleteToken: string,
): Promise<ShareResponse> {
  const response = await fetch(new URL('/v1/uploads', apiOrigin), {
    method: 'POST',
    headers: {
      'Content-Type': 'image/webp',
      'Idempotency-Key': idempotencyKey,
      'X-Delete-Token': deleteToken,
      'X-Turnstile-Token': turnstileToken,
    },
    body: blob,
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = (body as ShareErrorBody).error;
    throw new ShareApiError(
      response.status,
      typeof error === 'string' ? error : 'The share could not be created.',
    );
  }
  if (!isShareResponse(body))
    throw new ShareApiError(502, 'The share service returned invalid data.');
  return body;
}

export async function deleteShare(apiUrl: string, deleteToken: string): Promise<void> {
  const response = await fetch(apiUrl, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${deleteToken}` },
  });
  if (response.status !== 204 && response.status !== 404) {
    throw new ShareApiError(response.status, 'The share could not be deleted.');
  }
}
